import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  People,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface FeatureFlag {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UserFeatureFlag {
  id: number;
  user_id: number;
  feature_flag_id: number;
  feature_flag: FeatureFlag;
  granted_at: string;
  granted_by?: number;
}

export const AdminFeatureFlagsPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [selectedFlagForUsers, setSelectedFlagForUsers] = useState<FeatureFlag | null>(null);
  const [userFlags, setUserFlags] = useState<UserFeatureFlag[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    if (currentUser?.is_admin) {
      loadFeatureFlags();
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadFeatureFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getFeatureFlags();
      // Ensure response.data is always an array
      const data: any = response.data;
      const flags = Array.isArray(data) ? data : (data?.flags || data?.data || []);
      setFeatureFlags(flags);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load feature flags');
      setFeatureFlags([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.getAllUsers();
      // Ensure response.data is always an array
      const data: any = response.data;
      const users = Array.isArray(data) ? data : (data?.users || data?.data || []);
      setUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]); // Set empty array on error
    }
  };

  const loadUserFlags = async (flagId: number) => {
    try {
      const allUserFlags: UserFeatureFlag[] = [];
      for (const user of users) {
        try {
          const response = await apiService.getUserFeatureFlags(user.id);
          const flag = response.data.flags?.find((uf: UserFeatureFlag) => uf.feature_flag_id === flagId);
          if (flag) {
            allUserFlags.push(flag);
          }
        } catch (error) {
          // User might not have flags, skip
        }
      }
      setUserFlags(allUserFlags);
    } catch (error) {
      console.error('Error loading user flags:', error);
    }
  };

  const handleCreate = () => {
    setSelectedFlag(null);
    setFormData({ name: '', description: '', is_active: true });
    setOpenDialog(true);
  };

  const handleEdit = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setFormData({
      name: flag.name,
      description: flag.description || '',
      is_active: flag.is_active,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (flag: FeatureFlag) => {
    if (!window.confirm(`Are you sure you want to delete "${flag.name}"? This will revoke it from all users.`)) {
      return;
    }
    
    try {
      await apiService.deleteFeatureFlag(flag.id);
      loadFeatureFlags();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete feature flag');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Flag name is required');
      return;
    }

    try {
      if (selectedFlag) {
        await apiService.updateFeatureFlag(selectedFlag.id, formData);
      } else {
        await apiService.createFeatureFlag(formData);
      }
      setOpenDialog(false);
      loadFeatureFlags();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save feature flag');
    }
  };

  const handleOpenUserDialog = async (flag: FeatureFlag) => {
    setSelectedFlagForUsers(flag);
    await loadUserFlags(flag.id);
    setOpenUserDialog(true);
  };

  const handleGrantFlag = async (userId: number, flagId: number) => {
    try {
      await apiService.grantFeatureFlagToUser(userId, flagId);
      if (selectedFlagForUsers) {
        await loadUserFlags(selectedFlagForUsers.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to grant feature flag');
    }
  };

  const handleRevokeFlag = async (userId: number, flagId: number) => {
    if (!window.confirm('Are you sure you want to revoke this feature flag from the user?')) {
      return;
    }
    
    try {
      await apiService.revokeFeatureFlagFromUser(userId, flagId);
      if (selectedFlagForUsers) {
        await loadUserFlags(selectedFlagForUsers.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke feature flag');
    }
  };

  if (!currentUser?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You need admin privileges to access this section.</Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading feature flags..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Feature Flags Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Create Feature Flag
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {featureFlags.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No feature flags found. Create your first feature flag to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  featureFlags.map((flag) => {
                    const userCount = userFlags.filter(uf => uf.feature_flag_id === flag.id).length;
                    return (
                      <TableRow key={flag.id}>
                        <TableCell>
                          <Typography fontWeight={600}>{flag.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {flag.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={flag.is_active ? 'Active' : 'Inactive'}
                            color={flag.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            startIcon={<People />}
                            onClick={() => handleOpenUserDialog(flag)}
                          >
                            {userCount} users
                          </Button>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(flag)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(flag)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedFlag ? 'Edit Feature Flag' : 'Create Feature Flag'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            disabled={!!selectedFlag}
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedFlag ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Users with "{selectedFlagForUsers?.name}"
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const hasFlag = userFlags.some(uf => uf.user_id === user.id);
                  return (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell align="right">
                        {hasFlag ? (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => selectedFlagForUsers && handleRevokeFlag(user.id, selectedFlagForUsers.id)}
                          >
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<CheckCircle />}
                            onClick={() => selectedFlagForUsers && handleGrantFlag(user.id, selectedFlagForUsers.id)}
                          >
                            Grant
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

